import { SortDirectionType, SortingOptionsType } from '@components/design/migrated/Table';
import { Option, ShareType } from '@utils/pageConfig/Types';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { ProductDataType, ProductType } from '@pages/product-analyzer/Product';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';
import { BaseTimePeriod } from '@services/FiltersTypes';
import { CollectionLiveData } from '@components/collections/Model';
import { BaseApiParams } from '@services/Types';

export interface CategoryLeaderboardPgPage {
  leaderboardKpis: CategoryLeaderboardPgKpi;
  groupingKpis: CategoryLeaderboardGroupingPgKpi[];
}

export interface CategoryAndSegmentsLeaderboardGroupDetails {
  groupName: string;
  segmentOrPgValue: string[];
  showPerformanceWithin?: string;
  sortOrder?: number;
  isPreferenceOwner?: boolean;
}

export interface CategoryLeaderboardPg {
  segmentOrPg: string;
  country?: Country;
  marketRevenue: Kpi;
  marketVolume: Kpi;
  yourRevenue: Kpi;
  yourVolume: Kpi;
  marketPrice: Kpi;
  yourPrice: Kpi;
  marketExcludeRetailRevenue: Kpi;
  marketExcludeRetailVolume: Kpi;
  marketExcludeRetailPrice: Kpi;
  yourRevenueShare: KpiPercentage;
  yourVolumeShare: KpiPercentage;
  yourRevenueMix: KpiPercentage;
  yourVolumeMix: KpiPercentage;
  grouping: string;
}

export interface CategoryLeaderboardParam extends BaseApiParams, CollectionLiveData {
  growthType: GrowthTypes;
  shareType: ShareType;
  sortOption: SortingOptionsType;
  pageRequest: PageRequest;
  showPerformance?: string;
  dataType?: string;
  groups?: CategoryAndSegmentsLeaderboardGroupDetails[];
}

export interface PageRequest {
  page: number;
  size: number;
}

export interface CategoryLeaderboardConstructRequestObject extends CollectionLiveData {
  clientId: number;
  productType: ProductType;
  countryIds: string[];
  productGroup: Option[] | undefined;
  period: string;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  periodType: Periodicity;
  shareType: ShareType;
  showPerformance?: string;
  dataType?: string;
  groups?: CategoryAndSegmentsLeaderboardGroupDetails[];
}

export interface CategoryLeaderboardReq extends CollectionLiveData {
  clientId: number;
  productType: ProductType;
  productDataType: ProductDataType;
  periodType: string;
  period: string;
  comparisonPeriod: string;
  shareType: string;
  country: string[];
  productGroup: string[];
  priceBand: string[];
  showPerformance?: string;
  dataType?: string;
  groups?: CategoryAndSegmentsLeaderboardGroupDetails[];
  comparisonPeriodType?: string;
}

interface Pageable {
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPage: number;
}

interface Country {
  countryCode: string;
  countryName: string;
  currency: Currency;
}

interface Currency {
  currencyCode: string;
  currencyName: string;
  stCurrencyId: number;
}

export interface Kpi {
  value: number;
  percentage: number;
  actual: number;
}

export interface KpiPercentage {
  growthPercentage: number;
  sharePercentage: number;
}

interface CategoryLeaderboardPgKpi {
  content: CategoryLeaderboardPg[];
  pageable: Pageable;
}

export interface CategoryLeaderboardGroupingPgKpi {
  groupDetails: CategoryAndSegmentsLeaderboardGroupDetails;
  groupKpis: CategoryLeaderboardPg[];
}

export enum ColumnHeaderForCL {
  checkbox = 'checkbox',
  categoryGroup = 'categoryGroup',
  market = 'market',
  marketRevenue = 'marketRevenue',
  marketVolume = 'marketVolume',
  yourRevenue = 'yourRevenue',
  yourRevenueShare = 'yourRevenueShare',
  yourRevenueMix = 'yourRevenueMix',
  yourVolume = 'yourVolume',
  yourVolumeMix = 'yourVolumeMix',
  yourVolumeShare = 'yourVolumeShare',
  marketPrice = 'marketPrice',
  yourPrice = 'yourPrice',
  marketExcludeRetailRevenue = 'marketExcludeRetailRevenue',
  marketExcludeRetailVolume = 'marketExcludeRetailVolume',
  marketExcludeRetailPrice = 'marketExcludeRetailPrice',
}

export enum SortKeys {
  Absolute = '',
  Change = 'Change',
}

export enum SortLabels {
  Absolute = 'Absolute',
  Change = 'Change',
  Ascending = 'Ascending',
  Descending = 'Descending',
}

export enum ProductGroupKpiPercentage {
  YourRevenueShare = 'yourRevenueShare',
  YourVolumeShare = 'yourVolumeShare',
}

export const ColumnHeaderDisplayNameForCL = (volume: string = DEFAULT_VOLUME) => ({
  [ColumnHeaderForCL.checkbox]: 'CheckBox',
  [ColumnHeaderForCL.categoryGroup]: 'Category Group',
  [ColumnHeaderForCL.market]: 'Market',
  [ColumnHeaderForCL.marketRevenue]: 'Market Revenue',
  [ColumnHeaderForCL.marketVolume]: `Market ${volume}`,
  [ColumnHeaderForCL.yourRevenue]: 'Retailer Revenue',
  [ColumnHeaderForCL.yourRevenueShare]: 'Retailer Revenue Share',
  [ColumnHeaderForCL.yourRevenueMix]: 'Retailer Revenue Mix',
  [ColumnHeaderForCL.yourVolume]: `Retailer ${volume}`,
  [ColumnHeaderForCL.yourVolumeMix]: `Retailer ${volume} Mix`,
  [ColumnHeaderForCL.yourVolumeShare]: `Retailer ${volume} Share`,
  [ColumnHeaderForCL.marketPrice]: 'Market Price',
  [ColumnHeaderForCL.yourPrice]: 'Retailer Price',
  [ColumnHeaderForCL.marketExcludeRetailRevenue]: 'Market excl. Retailer Revenue',
  [ColumnHeaderForCL.marketExcludeRetailVolume]: `Market excl. Retailer ${volume}`,
  [ColumnHeaderForCL.marketExcludeRetailPrice]: 'Market excl. Retailer Price',
});

export const CategoryGroup = 'categoryGroup';

export const CategoryLeaderboardTableId = 'category-leaderboard';

export const DefaultSortingOptionsForCategoryLeaderboard: SortingOptionsType = {
  headerKey: ColumnHeaderForCL.yourRevenue,
  sortKey: SortKeys.Absolute,
  sortDir: SortDirectionType.desc ?? 'desc',
  label: SortLabels.Absolute,
};

export const chartViewTitle = 'Bar Chart';
export const tableViewTitle = 'Table';
export const doubleBarChartViewTitle = 'Double Bar Chart';

export interface GroupState {
  groupName?: string;
  enableIsEditGroupMode?: () => void;
  isGroupingHeader?: boolean;
  isGroupEmpty?: boolean;
  isGroupingProductGroup?: boolean;
  setIsGroupingRowExpanded?: (value: boolean) => void;
  onDeleteGroupPgs?: (productGroup: string) => void;
  isPreferenceOwner?: boolean;
}

export interface EditGroupMode {
  groupDetails: CategoryAndSegmentsLeaderboardGroupDetails;
  isEditMode: boolean;
}

export interface GetClassNamesParams {
  baseClasses?: string[];
  isSticky?: boolean;
  sortColumn?: string | null;
  keyHeader?: string | null;
  additionalClasses?: string[];
}
